import React, { useState, useMemo, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  LinearProgress,
  TextField,
  Typography,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import attendanceService from "../../services/attendanceService";

/**
 * QuickMarkAttendance — checkbox-based bulk attendance marking.
 *
 * Props:
 *  - selectedSlot: the selected lecture slot object from the sessions table
 *  - classId: selected class ID
 *  - subjectId: selected subject ID
 *  - date: YYYY-MM-DD string
 *  - batch: optional practical batch code
 *
 * Behavior:
 *  - Fetches roster for the selected lecture (students + current attendance status)
 *  - Already-present students: checked + locked (cannot unmark)
 *  - On-leave students: shown with a "Leave" badge, unchecked by default
 *  - Remaining (absent) students: unchecked by default
 *  - Submit marks only newly-selected students as PRESENT via markBulkAttendance
 *  - Preserves existing attendance method/time for already-marked students
 */
export default function QuickMarkAttendance({ selectedSlot, classId, subjectId, date, batch, onUploadComplete }) {
  // The top-level Subject filter is optional. When it's blank, the chosen lecture
  // row carries its own subjectId, so prefer the selected slot's subject and only
  // fall back to the filter prop. This keeps roster + marking targeted at the
  // correct subject even when no subject was picked in the filter.
  const effectiveSubjectId =
    selectedSlot?.subjectId ?? selectedSlot?.subject?.id ?? subjectId;
  const [roster, setRoster] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [result, setResult] = useState(null);

  // Track which student IDs are selected for PRESENT marking
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Monotonic request id: only the LATEST roster fetch may paint its result.
  // Prevents a slow response for a previously-selected lecture from repainting
  // the roster after the user switched to another lecture.
  const rosterReqSeqRef = useRef(0);

  const fetchRoster = useCallback(async () => {
    if (!selectedSlot || !classId || !date) return;
    const reqId = ++rosterReqSeqRef.current;
    // Clear the previous lecture's roster/selection IMMEDIATELY so switching
    // lectures never shows the prior lecture's marked students while loading
    // (the header updates from props, so a stale body was the visible bug).
    setRoster(null);
    setSelectedIds(new Set());
    setError("");
    setResult(null);
    setLoading(true);
    try {
      const params = {
        date,
        subjectId: effectiveSubjectId || undefined,
        timeTableSlotId: selectedSlot.timeTableSlotId,
        sessionKind: selectedSlot.sessionKind || undefined,
        practicalBatchCode: batch || selectedSlot.practicalBatchCode || undefined,
        limit: 500,
      };
      const r = await attendanceService.getRoster(classId, params);
      // Ignore a stale response if the user has since selected another lecture.
      if (reqId !== rosterReqSeqRef.current) return;
      const data = r?.data?.data || r?.data || {};
      setRoster(data);

      // Pre-select already-present students (they will be locked)
      const presentIds = new Set();
      (data?.data?.attendance || []).forEach((s) => {
        if (s?.id) presentIds.add(s.id);
      });
      setSelectedIds(presentIds);
    } catch (e) {
      if (reqId !== rosterReqSeqRef.current) return;
      setError(e?.response?.data?.message || e?.message || "Failed to fetch roster");
      setRoster(null);
    } finally {
      if (reqId === rosterReqSeqRef.current) setLoading(false);
    }
  }, [selectedSlot, classId, effectiveSubjectId, date, batch]);

  // Fetch roster when the selected lecture (or its context) changes. fetchRoster
  // resets roster/selection up front, so no separate reset is needed here.
  React.useEffect(() => {
    if (selectedSlot) {
      fetchRoster();
    } else {
      rosterReqSeqRef.current += 1; // invalidate any in-flight fetch
      setRoster(null);
      setSelectedIds(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSlot?.timeTableSlotId,
    selectedSlot?.date,
    selectedSlot?.startTime,
    selectedSlot?.endTime,
    selectedSlot?.practicalBatchCode,
    selectedSlot?.sessionKind,
    classId,
    subjectId,
    date,
    batch,
  ]);

  // Build combined student list with status
  const allStudents = useMemo(() => {
    if (!roster?.data) return [];
    const list = [];

    // Already marked present
    (roster.data.attendance || []).forEach((s) => {
      list.push({
        ...s,
        _status: "PRESENT",
        _locked: true, // Cannot unmark
        _method: s.attendance?.method || "—",
        _markedAt: s.attendance?.markedAt || null,
      });
    });

    // On leave
    (roster.data.onLeave || []).forEach((s) => {
      list.push({
        ...s,
        _status: "LEAVE",
        _locked: false,
        _method: null,
        _markedAt: null,
      });
    });

    // Remaining (absent)
    (roster.data.remaining || []).forEach((s) => {
      list.push({
        ...s,
        _status: "ABSENT",
        _locked: false,
        _method: null,
        _markedAt: null,
      });
    });

    return list;
  }, [roster]);

  // Filter by search
  const filteredStudents = useMemo(() => {
    if (!search.trim()) return allStudents;
    const q = search.toLowerCase().trim();
    return allStudents.filter((s) => {
      const name = (s.fullName || s.user?.fullName || "").toLowerCase();
      const loginId = (s.studentLoginID || "").toLowerCase();
      const roll = (s.rollNumber || "").toLowerCase();
      return name.includes(q) || loginId.includes(q) || roll.includes(q);
    });
  }, [allStudents, search]);

  // Counts
  const presentCount = useMemo(() => (roster?.data?.attendance || []).length, [roster]);
  const leaveCount = useMemo(() => (roster?.data?.onLeave || []).length, [roster]);
  const absentCount = useMemo(() => (roster?.data?.remaining || []).length, [roster]);
  const newlySelected = useMemo(() => {
    const presentIds = new Set((roster?.data?.attendance || []).map((s) => s.id));
    return [...selectedIds].filter((id) => !presentIds.has(id));
  }, [selectedIds, roster]);

  const handleToggle = (studentId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(studentId)) {
        next.delete(studentId);
      } else {
        next.add(studentId);
      }
      return next;
    });
  };

  const handleSelectAllAbsent = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      (roster?.data?.remaining || []).forEach((s) => {
        if (s?.id) next.add(s.id);
      });
      return next;
    });
  };

  const handleDeselectAllAbsent = () => {
    setSelectedIds((prev) => {
      const presentIds = new Set((roster?.data?.attendance || []).map((s) => s.id));
      const next = new Set(prev);
      // Keep only the locked (already-present) ones
      for (const id of next) {
        if (!presentIds.has(id)) next.delete(id);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!newlySelected.length) {
      toast.info("No new students selected to mark present.");
      return;
    }
    if (effectiveSubjectId == null || String(effectiveSubjectId).trim() === "") {
      toast.error("Selected lecture has no subject. Pick a lecture with a subject or choose a subject.");
      return;
    }

    setSubmitting(true);
    setError("");
    setResult(null);
    try {
      // Build a CSV with ONLY newly-selected students marked as PRESENT.
      // Do NOT include already-marked or unselected students — they remain unchanged.
      const rows = ["Roll No / Login ID,Status"];
      for (const studentId of newlySelected) {
        const student = allStudents.find((s) => s.id === studentId);
        if (!student) continue;
        const identifier = student.rollNumber || student.studentLoginID || String(student.id);
        rows.push(`${identifier},PRESENT`);
      }

      const csvContent = rows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv" });
      const file = new File([blob], "quick_mark_attendance.csv", { type: "text/csv" });

      const fd = new FormData();
      fd.append("date", date);
      fd.append("timeTableSlotId", String(selectedSlot.timeTableSlotId));
      fd.append("classId", String(classId));
      fd.append("subjectId", String(effectiveSubjectId));
      fd.append("preventOverwrite", "true");
      fd.append("file", file);

      const r = await attendanceService.uploadManualAttendanceCsv(fd);
      const data = r?.data?.data || null;
      setResult(data);
      const rr = data?.results || {};
      const sc = rr?.success?.length || 0;
      const fc = rr?.failed?.length || 0;
      const kc = rr?.skipped?.length || 0;

      if (fc > 0) {
        toast.error(`Completed with errors. Success: ${sc}, Failed: ${fc}, Skipped: ${kc}.`);
      } else {
        toast.success(`Attendance saved. ${sc} marked, ${kc} skipped.`);
      }

      // Refresh roster
      await fetchRoster();
      // Notify parent to refresh session list (updates "Manual Uploaded" column)
      if (typeof onUploadComplete === "function") onUploadComplete();
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to submit attendance");
      toast.error(e?.response?.data?.message || e?.message || "Failed to submit attendance");
    } finally {
      setSubmitting(false);
    }
  };

  if (!selectedSlot) {
    return (
      <Card>
        <CardContent>
          <Typography color="text.secondary">
            Select a lecture from the "Scheduled Lectures" table above, then use Quick Mark to toggle attendance.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="h6">
            Quick Mark Attendance
          </Typography>
          <Button size="small" variant="outlined" onClick={fetchRoster} disabled={loading}>
            {loading ? <CircularProgress size={16} sx={{ mr: 0.5 }} /> : null}
            Refresh
          </Button>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedSlot.date} | {selectedSlot.startTime} – {selectedSlot.endTime}
          {selectedSlot.practicalBatchCode ? ` | Batch: ${selectedSlot.practicalBatchCode}` : ""}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>Loading roster...</Typography>
          </Box>
        ) : roster ? (
          <>
            {/* Summary chips */}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              <Grid item>
                <Chip label={`Present: ${presentCount}`} color="success" size="small" />
              </Grid>
              <Grid item>
                <Chip label={`Absent: ${absentCount}`} color="error" size="small" variant="outlined" />
              </Grid>
              <Grid item>
                <Chip label={`On Leave: ${leaveCount}`} size="small" variant="outlined" />
              </Grid>
              {newlySelected.length > 0 && (
                <Grid item>
                  <Chip label={`New selections: ${newlySelected.length}`} color="primary" size="small" />
                </Grid>
              )}
            </Grid>

            {/* Search + select all/none */}
            <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search by name, roll no, or login ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 280, flex: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <Button size="small" variant="text" onClick={handleSelectAllAbsent}>
                Select All Absent
              </Button>
              <Button size="small" variant="text" onClick={handleDeselectAllAbsent}>
                Deselect All
              </Button>
            </Box>

            {submitting && <LinearProgress sx={{ mb: 1 }} />}

            {/* Student list */}
            <Box sx={{ maxHeight: 480, overflow: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
              {filteredStudents.length === 0 ? (
                <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
                  {search ? "No students match your search" : "No students found"}
                </Typography>
              ) : (
                filteredStudents.map((s) => {
                  const isChecked = selectedIds.has(s.id);
                  const isLocked = s._locked;
                  return (
                    <Box
                      key={s.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        py: 0.75,
                        px: 1,
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&:last-child": { borderBottom: "none" },
                        bgcolor: isLocked ? "action.hover" : "transparent",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isChecked}
                            disabled={isLocked || submitting}
                            onChange={() => handleToggle(s.id)}
                            color="success"
                            size="small"
                          />
                        }
                        label=""
                        sx={{ mr: 0.5, ml: 0 }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap sx={{ fontWeight: isLocked ? 600 : 400 }}>
                          {s.fullName || s.user?.fullName || "Unknown"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {s.rollNumber || s.studentLoginID || "—"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                        {s._status === "PRESENT" && (
                          <Chip
                            label={`Present • ${s._method}`}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                        {s._status === "LEAVE" && (
                          <Chip label="On Leave" size="small" color="warning" variant="outlined" />
                        )}
                        {s._status === "ABSENT" && !isChecked && (
                          <Chip label="Absent" size="small" color="error" variant="outlined" />
                        )}
                        {s._status === "ABSENT" && isChecked && (
                          <Chip label="→ Present" size="small" color="success" />
                        )}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Submit button */}
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", gap: 1.5, alignItems: "center" }}>
              {result && (
                <Typography variant="body2" color="success.main">
                  Last submission: {result.created?.length || 0} marked, {result.skipped?.length || 0} skipped
                </Typography>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={!newlySelected.length || submitting}
              >
                {submitting ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                Mark {newlySelected.length} Student{newlySelected.length !== 1 ? "s" : ""} Present
              </Button>
            </Box>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
